import {
  Card,
  Flex,
  Text,
  Badge,
  TextArea,
  Blockquote,
  BadgeProps,
} from '@radix-ui/themes';
import { Handle, Position } from 'reactflow';
import { useEffect } from 'react';
import { extractTemplateVars } from '../../../utils/template';

// enum ContentType {
//   NodeInput = 'NODE',
//   TextInput = 'TEXT',
// }

const parseTemplate = (text: string) => {
  const regex = /{{(.*?)}}/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the variable
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    // Add the variable
    parts.push({
      type: 'variable',
      content: match[1],
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last variable
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return parts;
};

const TemplateVariable = (props: BadgeProps) => (
  <Badge variant="surface" size="1" color="blue" {...props} />
);

interface TemplateBuilderProps {
  content: string;
  onChange: (content: string) => void;
  isEditing: boolean;
  onVariablesChange?: (variables: string[]) => void;
  showVariables?: boolean;
}

const TemplateBuilder = ({
  content,
  onChange,
  isEditing,
  onVariablesChange,
  showVariables = false,
}: TemplateBuilderProps) => {
  const variables = extractTemplateVars(content);

  // Notify parent component when variables change
  useEffect(() => {
    onVariablesChange?.(variables);
  }, [variables, onVariablesChange]);

  return (
    <Flex direction="column" gap="3">
      {isEditing ? (
        <TextArea
          size="1"
          value={content}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Blockquote size="1" color="gray">
          {parseTemplate(content).map((part, index) =>
            part.type === 'variable' ? (
              <TemplateVariable key={index}>{part.content}</TemplateVariable>
            ) : (
              <Text key={index} size="1">
                {part.content}
              </Text>
            ),
          )}
        </Blockquote>
      )}
      {showVariables && variables.length !== 0 && (
        <Flex direction="column" gap="1">
          <Text size="1" color="gray" weight="bold">
            Parameters
          </Text>
          {variables.map((variable) => (
            <Card key={variable} variant="surface">
              <Flex align="center">
                <TemplateVariable>{variable}</TemplateVariable>
              </Flex>
              <Handle
                id={variable}
                type="target"
                position={Position.Left}
                style={{ background: 'green', width: 8, height: 8 }}
              />
            </Card>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default TemplateBuilder;
